import { KeyboardEvent, FC } from 'react';
import { ApolloConsumer } from '@apollo/client';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { InputAutosuggest } from '@blueprint/text-input';
import {
    GEO_LOCATIONS_QUERY,
    ADDRESS_POSTCODE_SUGGESTION_QUERY
} from '@zoopla/listing-search-queries';
import {
    transformGeoLocationData,
    transformAddressPostcodeSuggestion
} from './transforms';

export type Region = 'ENGLAND' | 'SCOTLAND' | 'WALES';
type Props = {
    id?: string;
    searchSubmit: () => void;
    onInputTextChange?: (value: string) => void;
    value: string;
    locationTextValue?: string;
    region?: Region;
    searchType?: 'property' | 'house-prices';
    onLocationSelect: (location: LocationType) => void;
};

export type LocationType = {
    id: string;
    value: string | number | string[];
};

enum forSaleToRentPlaceholder {
    ENGLAND = 'eg. Oxford or NW3',
    SCOTLAND = 'e.g. Inverness or EH1',
    WALES = 'e.g. Maesteg or CF10'
}

const housePricesPlaceholder = 'e.g. Abbey Road, NW8 or Waterloo Station';

export const SimpleLocationSearchInput: FC<Props> = ({
    id,
    searchSubmit,
    value,
    searchType = 'property',
    locationTextValue = '',
    region = 'ENGLAND',
    onLocationSelect,
    onInputTextChange
}) => {
    const handleInputKeyDown = (evt: KeyboardEvent) => {
        evt.preventDefault();

        if (evt.key === 'Enter') {
            searchSubmit();
        }
    };

    const isHousePricesSearch = searchType === 'house-prices';

    return (
        <ApolloConsumer>
            {(client) => {
                const locationRegex = isHousePricesSearch
                    ? /([0-9A-Za-zÀ-ÖØ-öø-ÿ])/
                    : /([A-Za-zÀ-ÖØ-öø-ÿ])/;

                const placeholderText = isHousePricesSearch
                    ? housePricesPlaceholder
                    : forSaleToRentPlaceholder[region];

                const getGeoLocationData = async (value: string) => {
                    const { data } = await client.query({
                        query: GEO_LOCATIONS_QUERY,
                        variables: { locationPrefix: value }
                    });

                    return transformGeoLocationData(data.geoSuggestion);
                };

                const getAddressPostcodeSuggestion = async (value: string) => {
                    const { data } = await client.query({
                        query: ADDRESS_POSTCODE_SUGGESTION_QUERY,
                        variables: { addressPartial: value.toLowerCase() }
                    });

                    const locationTextValueSplit = locationTextValue.split(' ');
                    const firstElement = locationTextValueSplit[0];
                    const isFirstElementANumber = !isNaN(
                        parseInt(firstElement, 10)
                    );

                    const prefixElement = isFirstElementANumber
                        ? firstElement
                        : '';

                    return transformAddressPostcodeSuggestion(
                        data.getSuggestedAddresses,
                        prefixElement
                    );
                };

                const getSuggestionFunction = isHousePricesSearch
                    ? getAddressPostcodeSuggestion
                    : getGeoLocationData;

                const getSuggestionFunctionDebounced = AwesomeDebouncePromise(
                    getSuggestionFunction,
                    300,
                    {
                        onlyResolvesLast: true
                    }
                );

                return (
                    <InputAutosuggest
                        getData={getSuggestionFunctionDebounced}
                        suggestionSelectedAction={onLocationSelect}
                        placeholder={placeholderText}
                        validInputRegex={locationRegex}
                        id={id}
                        value={value}
                        autoComplete="off"
                        inputOnKeyDown={handleInputKeyDown}
                        inputTextChange={onInputTextChange}
                    />
                );
            }}
        </ApolloConsumer>
    );
};
